import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Command, Terminal, Shield } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'bootstrap'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api.get("/auth/bootstrap-status")
      .then(r => { if (r.data.needs_bootstrap) setMode("bootstrap"); })
      .catch(() => {})
      .finally(() => setChecked(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "bootstrap") {
        await register({ name, email, password, role: "super_admin" });
        toast.success("Super Admin dibuat. Selamat datang!");
      } else {
        await login(email, password);
        toast.success("Login berhasil");
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (mode === "bootstrap" ? "Bootstrap gagal" : "Login gagal"));
    } finally { setLoading(false); }
  };

  if (!checked) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500 label-mono"><span className="terminal-cursor">initializing</span></div>;
  }

  const isBootstrap = mode === "bootstrap";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-white">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-[#1F1F1F] relative overflow-hidden bg-grid">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
             style={{background: "radial-gradient(600px 400px at 20% 30%, rgba(0,122,255,0.35), transparent 60%), radial-gradient(500px 300px at 80% 80%, rgba(0,240,255,0.15), transparent 60%)"}} />
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center"><Command size={18} strokeWidth={2.5} /></div>
            <div>
              <div className="font-display font-black tracking-tight">DMCC</div>
              <div className="label-mono">command center</div>
            </div>
          </div>
        </div>

        <div className="relative space-y-6">
          <div className="label-mono">// welcome operator</div>
          <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95] tracking-tighter">
            Digital Marketing<br/>
            <span className="text-[#00F0FF]">Command Center.</span>
          </h1>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            Satu ruang kendali untuk 14 channel digital marketing. Real-time metrics,
            AI Analytics, laporan otomatis via WhatsApp & Email — semua dalam satu tempat.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            {[["14", "CHANNELS"], ["AI", "ANALYTICS"], ["24/7", "MONITOR"]].map(([v, l]) => (
              <div key={l} className="border border-[#1F1F1F] bg-[#0A0A0A] p-3">
                <div className="font-mono text-2xl font-medium">{v}</div>
                <div className="label-mono">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative label-mono text-[#00E676] flex items-center gap-2">
          <Terminal size={12} /> system online — all systems nominal
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="label-mono mb-2 flex items-center gap-2">
              {isBootstrap && <Shield size={12} className="text-[#00F0FF]" />}
              // {isBootstrap ? "first-time setup" : "authenticate"}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              {isBootstrap ? "Buat Super Admin Pertama" : "Masuk ke Command Center"}
            </h2>
            <p className="text-sm text-zinc-500 mt-2">
              {isBootstrap
                ? "Belum ada pengguna. Buat akun Super Admin pertama untuk memulai."
                : "Gunakan kredensial admin Anda untuk melanjutkan."}
            </p>
          </div>

          <div className="space-y-3">
            {isBootstrap && (
              <div>
                <label className="label-mono block mb-2">nama lengkap</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                  data-testid="bootstrap-name-input"
                  className="w-full bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-3 font-mono text-sm outline-none focus:border-[#007AFF]" />
              </div>
            )}
            <div>
              <label className="label-mono block mb-2">email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid={isBootstrap ? "bootstrap-email-input" : "login-email-input"}
                placeholder="admin@perusahaan.co.id"
                className="w-full bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-3 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div>
              <label className="label-mono block mb-2">password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                data-testid={isBootstrap ? "bootstrap-password-input" : "login-password-input"}
                placeholder="••••••••"
                className="w-full bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-3 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
          </div>

          <button type="submit" disabled={loading}
            data-testid={isBootstrap ? "bootstrap-submit-btn" : "login-submit-btn"}
            className="w-full bg-[#007AFF] hover:bg-[#0056b3] text-white py-3 font-mono text-sm uppercase tracking-[0.2em] transition disabled:opacity-50">
            {loading ? "memproses..." : (isBootstrap ? "→ buat super admin" : "→ masuk")}
          </button>

          <div className="pt-2 label-mono text-center text-zinc-600">
            {isBootstrap
              ? "// akun pertama otomatis menjadi super admin"
              : "// registrasi publik dinonaktifkan · hubungi super admin"}
          </div>
        </form>
      </div>
    </div>
  );
}
